/**
 * /checkin/admin — Check-In with Dr. C Admin Console (Phase 5).
 *
 * Single-page tabbed console for Dr. C. Only accessible with a
 * `role='checkin_admin'` JWT — the page redirects to /checkin/signin
 * when no admin token is present or the customer-summary probe returns
 * 403. Every mutation writes an admin_audit_log row server-side.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { CHECKIN_PALETTE } from "../_shell";
import { getCachedUser, getToken, clearAuth } from "@/lib/checkinApi";
import { adminApi } from "@/lib/kcisAdminApi";

/** Prefer the Check-In admin JWT if present; otherwise fall back to
 *  the KCIS super-admin JWT so a KCIS super admin who signed in via
 *  the main KCIS console can visit `/checkin/admin` without a second
 *  login. */
function resolveAdminToken() {
  const ci = getToken();  // checkin.token
  if (ci) return { token: ci, source: "checkin" };
  try {
    const kcis = localStorage.getItem("kcis_admin_token") || "";
    if (kcis) return { token: kcis, source: "kcis" };
  } catch { /* ignore */ }
  return { token: "", source: "" };
}

const TABS = [
  { id: "dashboard",   label: "Dashboard"    },
  { id: "customers",   label: "Customers"    },
  { id: "payments",    label: "Payments"     },
  { id: "entitlements",label: "Entitlements" },
  { id: "personality", label: "Voice Profile"},
  { id: "conversations", label: "Conversations" },
  { id: "safety",      label: "Safety"       },
  { id: "settings",    label: "Settings"     },
  { id: "audit",       label: "Audit Log"    },
];

export default function CheckInAdmin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [authed, setAuthed] = useState(null); // null=checking, true/false, 'redirect', 'denied'
  const cached = getCachedUser();

  useEffect(() => {
    let alive = true;
    // Accept the Check-In admin token OR a KCIS super-admin token
    // (single-sign-on). No token in either bucket → redirect to the
    // KCIS sign-in with a safe `next` back here.
    const resolved = resolveAdminToken();
    if (!resolved.token) {
      if (alive) setAuthed("redirect");
      return () => { alive = false; };
    }
    (async () => {
      try {
        // Probe an admin endpoint. `adminApi.dashboard` reads
        // `checkin.token`; when the caller only has a KCIS token we
        // fetch directly with the KCIS bearer instead.
        if (resolved.source === "kcis") {
          const API = process.env.REACT_APP_BACKEND_URL;
          const r = await fetch(`${API}/api/checkin/admin/operations/settings`, {
            headers: { Authorization: `Bearer ${resolved.token}` },
          });
          if (r.status === 401) {
            if (alive) setAuthed("redirect");
            return;
          }
          if (r.status === 403) {
            if (alive) setAuthed("denied");
            return;
          }
          if (!r.ok) throw new Error("probe_failed");
          if (alive) setAuthed(true);
          return;
        }
        await adminApi.dashboard();
        if (alive) setAuthed(true);
      } catch (e) {
        if (!alive) return;
        if (e && e.status === 403) setAuthed("denied");
        else if (e && e.status === 401) setAuthed("redirect");
        else setAuthed(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (authed === "redirect") {
    // Unauthenticated → send to the KCIS sign-in with a safe next
    // pointing back here. Preserves the existing KCIS auth flow;
    // never creates or exposes a separate Check-In admin credential.
    return <Navigate to="/admin/login?next=%2Fcheckin%2Fadmin" replace />;
  }
  if (authed === "denied") {
    return (
      <FullScreen>
        <div className="text-center max-w-sm" data-testid="admin-access-denied">
          <div className="text-2xl font-semibold mb-2">Access denied</div>
          <div className="text-sm mb-6" style={{ color: "#6C6478" }}>
            Your account does not have permission to open the Check In
            administration workspace. This area is restricted to KCIS
            super administrators.
          </div>
          <Link
            to="/kcis/admin"
            className="px-6 py-2 rounded-full font-medium inline-block"
            style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
            data-testid="admin-denied-back"
          >
            Return to KCIS admin
          </Link>
        </div>
      </FullScreen>
    );
  }

  if (authed === null) {
    return (
      <FullScreen>
        <div className="text-sm" style={{ color: CHECKIN_PALETTE.silver }}>
          Verifying admin access…
        </div>
      </FullScreen>
    );
  }
  if (authed === false) {
    return (
      <FullScreen>
        <div className="text-center max-w-sm" data-testid="admin-unauthorized">
          <div className="text-2xl font-semibold mb-2">Sign in required</div>
          <div className="text-sm mb-6" style={{ color: "#6C6478" }}>
            We couldn&apos;t verify your admin session. Sign in with your
            KCIS super-admin account to continue.
          </div>
          <button
            className="px-6 py-2 rounded-full font-medium"
            style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
            onClick={() => {
              clearAuth();
              try { localStorage.removeItem("kcis_admin_token"); } catch { /* noop */ }
              navigate("/admin/login?next=%2Fcheckin%2Fadmin");
            }}
            data-testid="admin-signout-cta"
          >
            Sign in
          </button>
        </div>
      </FullScreen>
    );
  }

  return (
    <div
      className="min-h-screen w-full"
      style={{ background: CHECKIN_PALETTE.paper, color: CHECKIN_PALETTE.ink,
                fontFamily: "'Inter', system-ui, sans-serif" }}
      data-testid="admin-console"
    >
      <TopBar admin={cached} onSignOut={() => {
        clearAuth();
        // Also drop the KCIS admin bridge token so signing out here
        // fully removes access from this workspace.
        try { localStorage.removeItem("kcis_admin_token"); } catch { /* noop */ }
        navigate("/admin/login?next=%2Fcheckin%2Fadmin");
      }} />
      <TabBar tab={tab} setTab={setTab} />
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6 py-6">
        {tab === "dashboard"    && <DashboardTab />}
        {tab === "customers"    && <CustomersTab />}
        {tab === "payments"     && <PaymentsTab />}
        {tab === "entitlements" && <EntitlementsTab />}
        {tab === "personality"  && <PersonalityTab />}
        {tab === "conversations"&& <ConversationsTab />}
        {tab === "safety"       && <SafetyTab />}
        {tab === "settings"     && <SettingsTab />}
        {tab === "audit"        && <AuditTab />}
      </div>
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{ background: CHECKIN_PALETTE.paper, color: CHECKIN_PALETTE.ink }}
    >
      {children}
    </div>
  );
}

function TopBar({ admin, onSignOut }) {
  return (
    <div
      className="w-full border-b"
      style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "white" }}
    >
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="inline-block w-6 h-6 rounded-full"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${CHECKIN_PALETTE.purpleSoft}, ${CHECKIN_PALETTE.purple} 65%, ${CHECKIN_PALETTE.ink})`,
            }}
          />
          <div>
            <div className="text-[13px] font-semibold" data-testid="admin-header">
              Check In Admin Console
            </div>
            <div className="text-[11px]" style={{ color: "#6C6478" }}>
              Dr. C · {admin?.email || ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(admin?.role === "super_admin") && (
            <Link to="/checkin/admin/conversations"
                  data-testid="admin-nav-intelligence"
                  className="text-xs underline"
                  style={{ color: CHECKIN_PALETTE.purple, fontWeight: 600 }}>
              Intelligence Center
            </Link>
          )}
          <Link to="/checkin/admin/operations"
                data-testid="admin-nav-operations"
                className="text-xs underline"
                style={{ color: CHECKIN_PALETTE.purple }}>
            Operations
          </Link>
          <button
            className="text-xs underline"
            onClick={onSignOut}
            data-testid="admin-signout"
            style={{ color: "#6C6478" }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function TabBar({ tab, setTab }) {
  return (
    <div
      className="sticky top-0 z-10 border-b overflow-x-auto"
      style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "white" }}
    >
      <div className="mx-auto max-w-6xl px-2 flex gap-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="px-3 py-3 text-[12px] whitespace-nowrap border-b-2 transition-colors"
            style={{
              borderColor: tab === t.id ? CHECKIN_PALETTE.purple : "transparent",
              color: tab === t.id ? CHECKIN_PALETTE.purple : "#6C6478",
              fontWeight: tab === t.id ? 600 : 400,
            }}
            data-testid={`admin-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════ Dashboard ═══════════════
function DashboardTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    adminApi.dashboard().then(setData).catch(e => setErr(e.message));
  }, []);
  if (err) return <ErrPanel msg={err} />;
  if (!data) return <SkeletonPanel testid="dashboard-loading" />;
  const { counters, pipeline, recent } = data;
  const health = pipeline?.status === "healthy";
  return (
    <div data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Metric label="Active memberships" value={counters.active_memberships} testid="dash-active-memberships" />
        <Metric label="Credits issued (all-time)" value={counters.total_credits_issued} testid="dash-issued" />
        <Metric label="Credits consumed" value={counters.total_credits_consumed} testid="dash-consumed" />
        <Metric label="Active users (7d)" value={counters.active_users_7d} testid="dash-active-users" />
        <Metric label="Total users" value={counters.total_users} testid="dash-total-users" />
        <Metric label="Safety events (7d)" value={counters.safety_events_7d} testid="dash-safety-7d" />
        <Metric label="Safety events (all)" value={counters.safety_events_total} testid="dash-safety-total" />
        <div
          className="rounded-xl border p-3"
          style={{ borderColor: health ? "#3F8F6A" : "#B4223C",
                    background: health ? "#F0FAF5" : "#FCEFF1" }}
          data-testid="dash-pipeline-status"
        >
          <div className="text-[10px] uppercase tracking-wide"
               style={{ color: "#6C6478" }}>Webhook pipeline</div>
          <div className="text-lg font-semibold"
               style={{ color: health ? "#3F8F6A" : "#B4223C" }}>
            {health ? "Healthy" : "Degraded"}
          </div>
          <div className="text-[11px]" style={{ color: "#6C6478" }}>
            {pipeline.signature_mismatches_24h} sig mismatches · {pipeline.events_received_24h} events received (24h)
          </div>
        </div>
        {(() => {
          const voice = data.voice || {};
          const vOk = voice.ok === true;
          const vColor = voice.ok === true ? "#3F8F6A" : voice.ok === false ? "#B4223C" : "#6C6478";
          const vBg = voice.ok === true ? "#F0FAF5" : voice.ok === false ? "#FCEFF1" : "#F5F3FA";
          return (
            <div className="rounded-xl border p-3"
                 style={{ borderColor: vColor, background: vBg }}
                 data-testid="dash-voice-status">
              <div className="text-[10px] uppercase tracking-wide" style={{ color: "#6C6478" }}>
                ElevenLabs voice
              </div>
              <div className="text-lg font-semibold" style={{ color: vColor }}>
                {voice.ok === true ? "Healthy" : voice.ok === false ? "Degraded" : "No renders yet"}
              </div>
              <div className="text-[11px]" style={{ color: "#6C6478" }}>
                {voice.reason ? `Last: ${voice.reason}` : (voice.at ? `Last render: ${voice.at.slice(11,19)} UTC` : "Waiting for first render")}
              </div>
            </div>
          );
        })()}
      </div>

      <RecentList title="Recent Talk Credit purchases" testid="dash-recent-credits"
                   items={recent.credit_purchases}
                   renderItem={e => `${e.granted_at?.slice(0,19)} · ${e.sku} · ${e.quantity}c · ${e.customer_id.slice(0,8)}`} />
      <RecentList title="Recent membership purchases" testid="dash-recent-memberships"
                   items={recent.membership_purchases}
                   renderItem={e => `${e.granted_at?.slice(0,19)} · ${e.sku} · ${e.customer_id.slice(0,8)}`} />
      <RecentList title="Recent reconciliations" testid="dash-recent-reconciles"
                   items={recent.reconciliations}
                   renderItem={e => `${e.granted_at?.slice(0,19)} · ${e.sku} · payment ${e.source_payment_id?.slice(0,10)}`} />
      <RecentList title="Rejected webhook events" testid="dash-rejected-webhooks"
                   items={recent.rejected_webhooks}
                   renderItem={e => `${e.created_at?.slice(0,19)} · ${e.event_type} · ${e.reason}`} />
      <RecentList title="Orphan Square payments" testid="dash-orphans"
                   items={recent.orphan_payments}
                   renderItem={e => `${e.created_at?.slice(0,19)} · ${e.reason} · payment ${e.payment_id?.slice(0,10) || "?"}`} />
      <RecentList title="Refunds / revoked entitlements" testid="dash-refunds"
                   items={recent.refunds}
                   renderItem={e => `${e.revoked_at?.slice(0,19) || e.granted_at?.slice(0,19)} · ${e.sku} · reason ${e.revoke_reason || "—"}`} />
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="rounded-xl border p-3"
         style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "white" }}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide" style={{ color: "#6C6478" }}>
        {label}
      </div>
      <div className="text-2xl font-semibold" style={{ color: CHECKIN_PALETTE.purple }}>
        {value}
      </div>
    </div>
  );
}

function RecentList({ title, items, renderItem, testid }) {
  return (
    <div className="mb-6" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider mb-2 font-semibold"
           style={{ color: CHECKIN_PALETTE.purple }}>{title}</div>
      <div className="rounded-xl border overflow-hidden"
           style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "white" }}>
        {(!items || items.length === 0) ? (
          <div className="p-4 text-sm" style={{ color: "#6C6478" }}>None</div>
        ) : items.map((it, i) => (
          <div key={it.id || i}
               className="p-3 text-[12px] border-b"
               style={{ borderColor: CHECKIN_PALETTE.silverSoft, color: "#3A344B" }}>
            {renderItem(it)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════ Customers ═══════════════
function CustomersTab() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const search = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const out = await adminApi.searchCustomers(q, 30);
      setItems(out.items || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [q]);

  return (
    <div data-testid="admin-customers">
      <div className="flex gap-2 mb-4">
        <input
          className="flex-1 rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: CHECKIN_PALETTE.silver }}
          placeholder="Search by email or first name"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === "Enter" && search()}
          data-testid="customer-search-input"
        />
        <button
          onClick={search}
          disabled={busy || q.length < 2}
          className="px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
          style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
          data-testid="customer-search-btn"
        >
          {busy ? "…" : "Search"}
        </button>
      </div>
      {err && <ErrPanel msg={err} />}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border overflow-hidden bg-white"
             style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
          {items.length === 0 && <div className="p-4 text-sm" style={{ color: "#6C6478" }}>No results yet — type an email or name.</div>}
          {items.map(it => (
            <button
              key={it.email}
              onClick={() => setSelected(null) || adminApi.customer(it.email).then(setSelected).catch(e => setErr(e.message))}
              className="w-full text-left p-3 border-b hover:bg-gray-50"
              style={{ borderColor: CHECKIN_PALETTE.silverSoft }}
              data-testid={`customer-row-${it.email}`}
            >
              <div className="text-sm font-medium">{it.email}</div>
              <div className="text-[11px]" style={{ color: "#6C6478" }}>
                {(it.user?.first_name || "—")} · user:{it.user ? "yes" : "no"} · customer:{it.customer ? "yes" : "no"}
              </div>
            </button>
          ))}
        </div>
        {selected && <CustomerDetail data={selected} onRefresh={() => adminApi.customer(selected.email).then(setSelected)} />}
      </div>
    </div>
  );
}

function CustomerDetail({ data, onRefresh }) {
  const [grantQty, setGrantQty] = useState(0);
  const [grantReason, setGrantReason] = useState("");
  const [grantRef, setGrantRef] = useState("");
  const [grantErr, setGrantErr] = useState("");
  const [grantOk, setGrantOk] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const qtyValid = Number.isInteger(Number(grantQty))
    && Number(grantQty) > 0 && Number(grantQty) <= 1000;
  const reasonValid = grantReason.trim().length >= 3 && grantReason.length <= 200;
  const formValid = qtyValid && reasonValid;
  const customerName = (data.checkin_user?.first_name
    || data.platform_customer?.first_name || "").trim();
  const customerLabel = customerName
    ? `${customerName} (${data.email})`
    : data.email;
  const currentBalance = Number(data.balance || 0);
  const expectedBalance = currentBalance + Number(grantQty || 0);

  function openConfirm() {
    setGrantErr(""); setGrantOk("");
    if (!formValid) return;
    setConfirmOpen(true);
  }

  function cancelConfirm() {
    if (processing) return;               // never cancel mid-request
    setConfirmOpen(false);
  }

  async function confirmGrant() {
    if (processing) return;               // block double-clicks
    setProcessing(true);
    setGrantErr(""); setGrantOk("");
    try {
      const out = await adminApi.grantCredits({
        email: data.email,
        quantity: Number(grantQty),
        reason: grantReason.trim(),
        reference: grantRef.trim() || undefined,
      });
      // Only surface success AFTER backend confirms.
      setGrantOk(`Complimentary credits added. New balance: ${out.balance_after}.`);
      setGrantQty(0); setGrantReason(""); setGrantRef("");
      setConfirmOpen(false);
      // Refresh selected customer's displayed balance from the server.
      onRefresh && onRefresh();
    } catch (e) {
      // Plain-language errors only — never leak IDs / stack traces / tokens.
      const raw = (e && e.message) ? String(e.message) : "";
      const status = e && e.status;
      let msg = "We could not add the credits. Please try again.";
      if (status === 401 || status === 403) {
        msg = "You are not authorized to grant Talk Credits.";
      } else if (status === 422) {
        msg = "The quantity or reason is invalid. Please review the form.";
      } else if (raw && raw.length < 160 && !/token|bearer|traceback|internal/i.test(raw)) {
        // Safe short message from server — pass it along.
        msg = raw;
      }
      setGrantErr(msg);
      // IMPORTANT: do NOT close the dialog and do NOT refresh balance on failure.
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div className="rounded-xl border p-4 bg-white"
         style={{ borderColor: CHECKIN_PALETTE.silverSoft }}
         data-testid="customer-detail">
      <div className="text-sm font-semibold mb-2">{data.email}</div>
      <div className="text-[11px] mb-3" style={{ color: "#6C6478" }}>
        Customer id: {data.platform_customer?.id?.slice(0,8) || "—"} · User id: {data.checkin_user?.id?.slice(0,8) || "—"}
      </div>
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wide" style={{ color: "#6C6478" }}>
          Talk Credit balance
        </div>
        <div className="text-3xl font-semibold" style={{ color: CHECKIN_PALETTE.purple }} data-testid="customer-balance">
          {data.balance}
        </div>
      </div>

      <div className="mb-3 rounded-lg border p-3" style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "#FAFAFB" }}>
        <div className="text-[11px] uppercase tracking-wide mb-2 font-semibold" style={{ color: CHECKIN_PALETTE.purple }}>
          Break-glass grant
        </div>
        <div className="flex flex-col gap-2">
          <input type="number" min="1" max="1000" value={grantQty} onChange={e => setGrantQty(e.target.value)}
                 className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
                 placeholder="Quantity" data-testid="grant-qty" />
          <input value={grantReason} onChange={e => setGrantReason(e.target.value)} maxLength={200}
                 className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
                 placeholder="Reason (required)" data-testid="grant-reason" />
          <input value={grantRef} onChange={e => setGrantRef(e.target.value)} maxLength={120}
                 className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
                 placeholder="Reference (optional, for idempotency)" data-testid="grant-ref" />
          <button onClick={openConfirm}
                  disabled={!formValid}
                  className="rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-40"
                  style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                  data-testid="grant-submit">
            Review &amp; Confirm Grant
          </button>
          {grantErr && <div className="text-xs" style={{ color: "#B4223C" }} data-testid="grant-error">{grantErr}</div>}
          {grantOk && <div className="text-xs" style={{ color: "#3F8F6A" }} data-testid="grant-success">{grantOk}</div>}
        </div>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(20, 15, 30, 0.55)" }}
          data-testid="grant-confirm-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="grant-confirm-title"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl border"
               style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
            <div id="grant-confirm-title"
                 className="text-[11px] uppercase tracking-wider font-semibold mb-2"
                 style={{ color: CHECKIN_PALETTE.purple }}>
              Confirm complimentary Talk Credit grant
            </div>
            <p className="text-sm text-slate-800 leading-relaxed" data-testid="grant-confirm-summary">
              You are adding <strong data-testid="grant-confirm-qty">{Number(grantQty)}</strong> complimentary
              Talk Credits to <strong data-testid="grant-confirm-customer">{customerLabel}</strong>. Their balance
              will increase from <strong data-testid="grant-confirm-old-balance">{currentBalance}</strong> to <strong data-testid="grant-confirm-new-balance">{expectedBalance}</strong>.
            </p>
            <div className="mt-3 rounded-lg border p-3 text-[12px]"
                 style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "#FAFAFB" }}>
              <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "#6C6478" }}>Reason</div>
              <div className="text-slate-800" data-testid="grant-confirm-reason">{grantReason.trim()}</div>
              {grantRef.trim() && (
                <>
                  <div className="text-[10px] uppercase tracking-wide mt-2 mb-1" style={{ color: "#6C6478" }}>Reference</div>
                  <div className="text-slate-800 break-all" data-testid="grant-confirm-reference">{grantRef.trim()}</div>
                </>
              )}
            </div>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              Confirming will change this customer&apos;s spendable Talk Credit balance immediately.
              No Square charge will be created.
            </p>

            {processing && (
              <div className="mt-3 text-[12px]" style={{ color: CHECKIN_PALETTE.purple }} data-testid="grant-confirm-processing">
                Processing grant…
              </div>
            )}
            {grantErr && (
              <div className="mt-3 text-[12px]" style={{ color: "#B4223C" }} data-testid="grant-confirm-error">
                {grantErr}
              </div>
            )}

            <div className="mt-5 flex gap-2 justify-end">
              <button
                type="button"
                onClick={cancelConfirm}
                disabled={processing}
                className="rounded-lg px-4 py-2 text-sm font-medium border disabled:opacity-40"
                style={{ borderColor: CHECKIN_PALETTE.silver, color: "#3A344B", background: "white" }}
                data-testid="grant-confirm-cancel"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={confirmGrant}
                disabled={processing || !formValid}
                className="rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-40"
                style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                data-testid="grant-confirm-submit"
              >
                {processing ? "Confirming…" : "Confirm Credit Grant"}
              </button>
            </div>
          </div>
        </div>
      )}

      <RelList title={`Entitlements (${data.entitlements?.length || 0})`} items={data.entitlements}
                testid="cust-entitlements"
                renderItem={e => `${e.granted_at?.slice(0,10)} · ${e.kind} · ${e.sku} · qty ${e.quantity} · ${e.status}`} />
      <RelList title={`Ledger (${data.ledger?.length || 0})`} items={data.ledger}
                testid="cust-ledger"
                renderItem={e => `${e.created_at?.slice(0,19)} · ${e.kind} · ${e.quantity > 0 ? "+" : ""}${e.quantity} · ${e.source_sku || e.reason || ""}`} />
      <RelList title={`Recent checkouts (${data.sessions?.length || 0})`} items={data.sessions}
                testid="cust-sessions"
                renderItem={s => `${s.created_at?.slice(0,19)} · ${s.status} · ${s.sku} · $${(s.amount_total_cents/100).toFixed(2)}`} />
    </div>
  );
}

function RelList({ title, items, renderItem, testid }) {
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "#6C6478" }}>{title}</div>
      <div className="rounded border overflow-hidden" style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
        {(!items || items.length === 0) ? (
          <div className="p-2 text-[11px]" style={{ color: "#6C6478" }}>None</div>
        ) : items.slice(0, 8).map((it, i) => (
          <div key={it.id || i}
               className="p-2 text-[11px] border-b"
               style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
            {renderItem(it)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════ Payments ═══════════════
function PaymentsTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [since, setSince] = useState(30);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const out = await adminApi.squarePayments(since, 50);
      setRows(out.items || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [since]);

  useEffect(() => { load(); }, [load]);

  async function reconcile(paymentId) {
    if (!confirm("Reconcile this payment? Idempotent — safe to re-run.")) return;
    try {
      const out = await adminApi.reconcileOne(paymentId);
      alert(`Result: ${out.result.reason || (out.result.handled ? "granted" : "unhandled")}`);
      await load();
    } catch (e) { alert(e.message); }
  }

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center gap-2 mb-3">
        <select value={since} onChange={e => setSince(Number(e.target.value))}
                className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
                data-testid="payments-since">
          {[7, 14, 30, 60, 90].map(n => <option key={n} value={n}>Last {n} days</option>)}
        </select>
        <button onClick={load} disabled={busy}
                className="px-3 py-1 rounded text-sm font-medium disabled:opacity-40"
                style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                data-testid="payments-refresh">
          {busy ? "…" : "Refresh"}
        </button>
      </div>
      {err && <ErrPanel msg={err} />}
      <div className="rounded-xl border bg-white overflow-x-auto"
           style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
        <table className="w-full text-[11px]" data-testid="payments-table">
          <thead>
            <tr style={{ background: "#F5F3FA" }}>
              <th className="text-left p-2">Date</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Amount</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">App / SKU</th>
              <th className="text-left p-2">Entitlement</th>
              <th className="text-left p-2">Flags</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const flags = [];
              if (r.flags.entitlement_missing) flags.push("no-ent");
              if (r.flags.customer_missing) flags.push("no-cust");
              if (r.flags.metadata_invalid) flags.push("no-meta");
              if (r.flags.refunded) flags.push("refunded");
              return (
                <tr key={r.payment_id} className="border-t" style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
                  <td className="p-2">{r.created_at?.slice(0,19)}</td>
                  <td className="p-2">{r.status}</td>
                  <td className="p-2">${(r.amount_cents/100).toFixed(2)}</td>
                  <td className="p-2 truncate max-w-[160px]">{r.client_email || r.buyer_email || "—"}</td>
                  <td className="p-2">{r.app_slug || "—"} / {r.sku || "—"}</td>
                  <td className="p-2">{r.entitlement_id ? r.entitlement_id.slice(0,8) : "—"}</td>
                  <td className="p-2">
                    {flags.length === 0 ? <span style={{ color: "#3F8F6A" }}>ok</span>
                                          : <span style={{ color: "#B4223C" }}>{flags.join(", ")}</span>}
                  </td>
                  <td className="p-2">
                    {r.flags.entitlement_missing && !r.flags.metadata_invalid && (
                      <button onClick={() => reconcile(r.payment_id)}
                              className="text-[11px] px-2 py-1 rounded"
                              style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                              data-testid={`reconcile-${r.payment_id}`}>
                        Reconcile
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center text-sm" style={{ color: "#6C6478" }}>No payments in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════ Entitlements ═══════════════
function EntitlementsTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState({ email: "", app_slug: "checkin", kind: "", status: "" });
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const out = await adminApi.entitlements(q);
      setRows(out.items || []);
    } catch (e) { setErr(e.message); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  async function revoke(id) {
    const reason = window.prompt("Reason for revoking? (required, ≥3 chars)");
    if (!reason || reason.length < 3) return;
    if (!window.confirm(`Revoke entitlement ${id.slice(0,8)}?`)) return;
    try {
      await adminApi.revokeEntitlement(id, reason);
      await load();
    } catch (e) { alert(e.message); }
  }

  return (
    <div data-testid="admin-entitlements">
      <div className="flex flex-wrap gap-2 mb-3">
        <input placeholder="email" value={q.email} onChange={e => setQ({...q, email: e.target.value})}
               className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
               data-testid="ent-filter-email" />
        <select value={q.kind} onChange={e => setQ({...q, kind: e.target.value})}
                className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
                data-testid="ent-filter-kind">
          <option value="">Any kind</option>
          <option value="talk_credit_grant">talk_credit_grant</option>
          <option value="checkin_membership_active">checkin_membership_active</option>
          <option value="coaching_program_access">coaching_program_access</option>
        </select>
        <select value={q.status} onChange={e => setQ({...q, status: e.target.value})}
                className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
                data-testid="ent-filter-status">
          <option value="">Any status</option>
          <option value="active">active</option>
          <option value="revoked">revoked</option>
        </select>
        <button onClick={load} className="px-3 py-1 rounded text-sm font-medium"
                style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                data-testid="ent-search-btn">Search</button>
      </div>
      {err && <ErrPanel msg={err} />}
      <div className="rounded-xl border bg-white overflow-x-auto"
           style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: "#F5F3FA" }}>
              <th className="text-left p-2">Granted</th>
              <th className="text-left p-2">Kind</th>
              <th className="text-left p-2">SKU</th>
              <th className="text-left p-2">Qty</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Customer</th>
              <th className="text-left p-2">Source</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t" style={{ borderColor: CHECKIN_PALETTE.silverSoft }} data-testid={`ent-row-${r.id}`}>
                <td className="p-2">{r.granted_at?.slice(0,19)}</td>
                <td className="p-2">{r.kind}</td>
                <td className="p-2">{r.sku}</td>
                <td className="p-2">{r.quantity}</td>
                <td className="p-2">{r.status}</td>
                <td className="p-2">{r.customer_id?.slice(0,8)}</td>
                <td className="p-2">{r.source_payment_id?.slice(0,8) || r.source_event_id?.slice(0,20) || "—"}</td>
                <td className="p-2">
                  {r.status === "active" && (
                    <button onClick={() => revoke(r.id)}
                            className="text-[11px] px-2 py-1 rounded"
                            style={{ background: "#B4223C", color: "white" }}
                            data-testid={`revoke-${r.id}`}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center text-sm" style={{ color: "#6C6478" }}>No entitlements match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════ Personality ═══════════════
function PersonalityTab() {
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [components, setComponents] = useState(null);
  const [notes, setNotes] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [testInput, setTestInput] = useState("How do I stop overthinking?");
  const [testDraft, setTestDraft] = useState("");
  const [testPub, setTestPub] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  async function reload() {
    setErr(""); setOk("");
    try {
      const out = await adminApi.personality();
      setData(out);
      const startingComps = out.draft?.components || out.published?.components || out.seed;
      setComponents({ ...startingComps });
      const v = await adminApi.personalityVersions();
      setVersions(v.items || []);
    } catch (e) { setErr(e.message); }
  }
  useEffect(() => { reload(); }, []);

  function updateComp(key, patch) {
    setComponents(c => ({ ...c, [key]: { ...(c[key] || {}), ...patch } }));
  }

  async function saveDraft() {
    setSaving(true); setErr(""); setOk("");
    try {
      await adminApi.saveDraft(components, notes);
      setOk("Draft saved.");
      await reload();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }
  async function publish() {
    if (!window.confirm("Publish the current draft as the active Voice Profile?")) return;
    try {
      await adminApi.publishDraft();
      setOk("Published. Live starting next conversation turn.");
      await reload();
    } catch (e) { setErr(e.message); }
  }
  async function rollback(v) {
    if (!window.confirm(`Roll back to version ${v}?`)) return;
    try {
      await adminApi.rollback(v);
      setOk(`Rolled back to v${v}.`);
      await reload();
    } catch (e) { setErr(e.message); }
  }
  async function testCompare() {
    setTestBusy(true); setTestDraft(""); setTestPub("");
    try {
      const out = await adminApi.replay({ user_text: testInput, versions: ["draft", "published"] });
      const byVer = {};
      (out.responses || []).forEach(r => { byVer[r.version] = r; });
      setTestDraft(byVer.draft?.assistant_text || byVer.draft?.error || "(no draft — using seed)");
      setTestPub(byVer.published?.assistant_text || byVer.published?.error || "(no published version — using seed)");
    } catch (e) { setErr(e.message); } finally { setTestBusy(false); }
  }

  if (!data || !components) return <SkeletonPanel testid="personality-loading" />;
  const order = data.order || Object.keys(components);
  const activeCount = order.filter(k => components[k]?.active !== false && (components[k]?.body || "").trim().length > 0).length;

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="admin-personality">
      <div className="md:col-span-1">
        <div className="text-[11px] uppercase font-semibold mb-2" style={{ color: CHECKIN_PALETTE.purple }}>
          Components ({activeCount}/{order.length} active)
        </div>
        <div className="rounded-xl border bg-white overflow-hidden"
             style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
          {order.map(k => {
            const comp = components[k] || {};
            const active = comp.active !== false && (comp.body || "").trim().length > 0;
            return (
              <button key={k}
                      onClick={() => setSelectedKey(k)}
                      className="w-full text-left px-3 py-2 border-b text-[12px] hover:bg-gray-50"
                      style={{ borderColor: CHECKIN_PALETTE.silverSoft,
                                background: selectedKey === k ? "#F5F3FA" : "white" }}
                      data-testid={`comp-${k}`}>
                <div className="flex items-center justify-between">
                  <span>{comp.title || k.replace(/_/g, " ")}</span>
                  <span className="text-[10px]" style={{ color: active ? "#3F8F6A" : "#B4223C" }}>
                    {active ? "on" : "off"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 rounded-xl border p-3 bg-white" style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
          <div className="text-[10px] uppercase mb-1" style={{ color: "#6C6478" }}>Draft notes</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                    className="w-full rounded border px-2 py-1 text-[12px]"
                    style={{ borderColor: CHECKIN_PALETTE.silver }}
                    placeholder="What did you change and why?"
                    data-testid="draft-notes" />
          <div className="flex gap-2 mt-2">
            <button onClick={saveDraft} disabled={saving}
                    className="px-3 py-1 rounded text-sm font-medium disabled:opacity-40"
                    style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                    data-testid="draft-save">
              {saving ? "…" : "Save draft"}
            </button>
            <button onClick={publish}
                    className="px-3 py-1 rounded text-sm font-medium"
                    style={{ background: "#3F8F6A", color: "white" }}
                    data-testid="draft-publish">
              Publish
            </button>
          </div>
          {err && <div className="text-xs mt-2" style={{ color: "#B4223C" }}>{err}</div>}
          {ok && <div className="text-xs mt-2" style={{ color: "#3F8F6A" }} data-testid="personality-ok">{ok}</div>}
        </div>

        <div className="mt-4">
          <div className="text-[11px] uppercase font-semibold mb-2" style={{ color: CHECKIN_PALETTE.purple }}>Version history</div>
          <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
            {versions.length === 0 && <div className="p-3 text-[12px]" style={{ color: "#6C6478" }}>No versions yet.</div>}
            {versions.map(v => (
              <div key={v.version} className="p-2 border-b text-[11px] flex items-center justify-between"
                   style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
                <div>
                  v{v.version} · <b>{v.status}</b> · {(v.published_at || v.updated_at || "").slice(0,10)}
                </div>
                {v.status !== "published" && v.status !== "draft" && (
                  <button onClick={() => rollback(v.version)}
                          className="text-[11px] underline"
                          data-testid={`rollback-${v.version}`}>
                    Rollback
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="md:col-span-2">
        {selectedKey ? (
          <ComponentEditor
            k={selectedKey}
            comp={components[selectedKey] || {}}
            onChange={patch => updateComp(selectedKey, patch)}
          />
        ) : (
          <div className="rounded-xl border p-6 bg-white text-sm"
               style={{ borderColor: CHECKIN_PALETTE.silverSoft, color: "#6C6478" }}>
            Select a component on the left to edit.
          </div>
        )}

        <div className="mt-4 rounded-xl border p-4 bg-white"
             style={{ borderColor: CHECKIN_PALETTE.silverSoft }}
             data-testid="personality-test-pane">
          <div className="text-[11px] uppercase font-semibold mb-2" style={{ color: CHECKIN_PALETTE.purple }}>
            Test with Dr. C — Draft vs Published
          </div>
          <textarea value={testInput} onChange={e => setTestInput(e.target.value)} rows={2}
                    className="w-full rounded border px-2 py-1 text-sm"
                    style={{ borderColor: CHECKIN_PALETTE.silver }}
                    data-testid="test-input" />
          <button onClick={testCompare} disabled={testBusy || !testInput.trim()}
                  className="mt-2 px-3 py-1 rounded text-sm font-medium disabled:opacity-40"
                  style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                  data-testid="test-compare">
            {testBusy ? "Running…" : "Compare responses"}
          </button>
          {(testDraft || testPub) && (
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              <TestPane label="Draft" text={testDraft} testid="test-draft-response" />
              <TestPane label="Published" text={testPub} testid="test-pub-response" />
            </div>
          )}
        </div>

        <VoiceDeliveryPanel />
      </div>
    </div>
  );
}

// ═══════════════ Voice Delivery (Dr. C ElevenLabs settings) ═══════════════
function VoiceDeliveryPanel() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sampleKey, setSampleKey] = useState("warm_greeting");
  const [customText, setCustomText] = useState("");
  const [busy, setBusy] = useState(false);
  const [audioB64, setAudioB64] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const reload = useCallback(async () => {
    setErr("");
    try {
      const out = await adminApi.voiceDelivery();
      setData(out);
      // Start from published (falling back to defaults) so edits are additive.
      setSettings({ ...(out.defaults || {}), ...(out.published || {}) });
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  function updateSetting(k, v) {
    setSettings(s => ({ ...(s || {}), [k]: v }));
  }

  async function preview() {
    setBusy(true); setErr(""); setOk(""); setAudioB64("");
    try {
      const out = await adminApi.voicePreview(settings, sampleKey, customText.trim() || null);
      setAudioB64(out.mp3_base64);
      setOk(`Rendered ${(out.mp3_base64.length * 3 / 4 / 1024).toFixed(1)} KB · ${out.generated_fresh ? "fresh" : "cached"}`);
    } catch (e) {
      const status = e.raw?.detail?.voice_status;
      const reason = status?.reason ? ` (last ElevenLabs status: ${status.reason})` : "";
      setErr(`${e.message}${reason}`);
    } finally { setBusy(false); }
  }

  async function publish() {
    if (!window.confirm(
      "Publish these Voice Delivery settings? Dr. C's voice will use them for all NEW audio renders (cached audio keeps playing with older settings). This action is audit-logged."
    )) return;
    setBusy(true); setErr(""); setOk("");
    try {
      await adminApi.saveSettings("voice_delivery", settings, true);
      setOk("Published. New voice renders now use these settings.");
      await reload();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  if (!data || !settings) return <SkeletonPanel testid="voice-delivery-loading" />;

  const status = data.voice_status || {};
  const statusOk = status.ok === true;
  const statusText = status.ok === true ? "Healthy"
                    : status.ok === false ? `Degraded — ${status.reason || "unknown"}`
                    : "No renders yet";
  const statusColor = status.ok === true ? "#3F8F6A"
                    : status.ok === false ? "#B4223C"
                    : "#6C6478";

  return (
    <div className="mt-4 rounded-xl border p-4 bg-white"
         style={{ borderColor: CHECKIN_PALETTE.silverSoft }}
         data-testid="voice-delivery-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase font-semibold" style={{ color: CHECKIN_PALETTE.purple }}>
            Voice Delivery (Dr. C · ElevenLabs)
          </div>
          <div className="text-[11px] mt-1" style={{ color: "#6C6478" }}>
            Tune pacing and warmth. Preview before publishing.
          </div>
        </div>
        <div className="text-right" data-testid="voice-status-chip">
          <div className="text-[10px] uppercase" style={{ color: "#6C6478" }}>ElevenLabs</div>
          <div className="text-sm font-semibold" style={{ color: statusColor }}>{statusOk ? "●" : "○"} {statusText}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <SliderRow label="Stability" min={0} max={1} step={0.01}
                    value={Number(settings.stability)} testid="vd-stability"
                    onChange={v => updateSetting("stability", v)} />
        <SliderRow label="Similarity" min={0} max={1} step={0.01}
                    value={Number(settings.similarity_boost)} testid="vd-similarity"
                    onChange={v => updateSetting("similarity_boost", v)} />
        <SliderRow label="Style (theatricality)" min={0} max={1} step={0.01}
                    value={Number(settings.style)} testid="vd-style"
                    onChange={v => updateSetting("style", v)} />
        <SliderRow label="Speed (pacing)" min={0.7} max={1.2} step={0.01}
                    value={Number(settings.speed)} testid="vd-speed"
                    onChange={v => updateSetting("speed", v)} />
      </div>

      <div className="flex flex-wrap gap-3 items-center mb-3 text-[12px]">
        <label className="flex items-center gap-1" data-testid="vd-speaker-boost-wrap">
          <input type="checkbox" checked={!!settings.use_speaker_boost}
                 onChange={e => updateSetting("use_speaker_boost", e.target.checked)}
                 data-testid="vd-speaker-boost" />
          Speaker boost
        </label>
        <label className="flex items-center gap-1" data-testid="vd-pauses-wrap">
          <input type="checkbox" checked={!!settings.add_reflective_pauses}
                 onChange={e => updateSetting("add_reflective_pauses", e.target.checked)}
                 data-testid="vd-pauses" />
          Add reflective pauses
        </label>
        <label className="flex items-center gap-1">
          Max chars
          <input type="number" min="200" max="10000" value={settings.max_chars || 4000}
                 onChange={e => updateSetting("max_chars", Number(e.target.value))}
                 className="w-20 rounded border px-1 py-0.5" style={{ borderColor: CHECKIN_PALETTE.silver }}
                 data-testid="vd-max-chars" />
        </label>
        <label className="flex items-center gap-1">
          Retries
          <input type="number" min="0" max="5" value={settings.retry_attempts ?? 2}
                 onChange={e => updateSetting("retry_attempts", Number(e.target.value))}
                 className="w-14 rounded border px-1 py-0.5" style={{ borderColor: CHECKIN_PALETTE.silver }}
                 data-testid="vd-retries" />
        </label>
      </div>

      <div className="rounded-lg border p-3" style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "#FAFAFB" }}>
        <div className="text-[10px] uppercase mb-1" style={{ color: "#6C6478" }}>Test sample</div>
        <div className="flex flex-wrap gap-1 mb-2">
          {(data.samples || []).map(s => (
            <button key={s.key}
                    onClick={() => { setSampleKey(s.key); setCustomText(""); }}
                    className="px-2 py-1 rounded text-[11px]"
                    style={{
                      background: sampleKey === s.key && !customText.trim() ? CHECKIN_PALETTE.purple : "white",
                      color: sampleKey === s.key && !customText.trim() ? "white" : CHECKIN_PALETTE.ink,
                      border: `1px solid ${CHECKIN_PALETTE.silverSoft}`,
                    }}
                    data-testid={`voice-sample-${s.key}`}>
              {s.key.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <div className="text-[11px] italic mb-2" style={{ color: "#6C6478" }}>
          {customText.trim() ? "(using custom text)" : (data.samples || []).find(s => s.key === sampleKey)?.text}
        </div>
        <textarea value={customText} onChange={e => setCustomText(e.target.value)} rows={2}
                  className="w-full rounded border px-2 py-1 text-[12px]"
                  style={{ borderColor: CHECKIN_PALETTE.silver }}
                  placeholder="Or paste custom text to preview…"
                  maxLength={4000}
                  data-testid="voice-custom-text" />
        <div className="flex gap-2 mt-2">
          <button onClick={preview} disabled={busy}
                  className="px-3 py-1 rounded text-sm font-medium disabled:opacity-40"
                  style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                  data-testid="voice-preview">
            {busy ? "Rendering…" : "Preview delivery"}
          </button>
          <button onClick={publish} disabled={busy}
                  className="px-3 py-1 rounded text-sm font-medium disabled:opacity-40"
                  style={{ background: "#3F8F6A", color: "white" }}
                  data-testid="voice-publish">
            Publish settings
          </button>
        </div>
        {err && <div className="text-xs mt-2" style={{ color: "#B4223C" }} data-testid="voice-error">{err}</div>}
        {ok && <div className="text-xs mt-2" style={{ color: "#3F8F6A" }} data-testid="voice-ok">{ok}</div>}
        {audioB64 && (
          <audio controls autoPlay className="mt-3 w-full" data-testid="voice-audio"
                 src={`data:audio/mpeg;base64,${audioB64}`} />
        )}
      </div>
    </div>
  );
}

function SliderRow({ label, min, max, step, value, onChange, testid }) {
  const v = Number.isFinite(value) ? value : min;
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="min-w-[110px]" style={{ color: "#4A4256" }}>{label}</span>
      <input type="range" min={min} max={max} step={step} value={v}
             onChange={e => onChange(Number(e.target.value))}
             className="flex-1" data-testid={testid} />
      <span className="w-10 text-right tabular-nums" style={{ color: CHECKIN_PALETTE.purple }}>
        {v.toFixed(2)}
      </span>
    </div>
  );
}

function ComponentEditor({ k, comp, onChange }) {
  return (
    <div className="rounded-xl border p-4 bg-white" style={{ borderColor: CHECKIN_PALETTE.silverSoft }}
         data-testid={`comp-editor-${k}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">{comp.title || k}</div>
        <label className="text-[11px] flex items-center gap-1">
          <input type="checkbox" checked={comp.active !== false}
                 onChange={e => onChange({ active: e.target.checked })}
                 data-testid={`comp-active-${k}`} />
          Active
        </label>
      </div>
      <input value={comp.title || ""} onChange={e => onChange({ title: e.target.value })}
             placeholder="Title" maxLength={80}
             className="w-full rounded border px-2 py-1 text-sm mb-2"
             style={{ borderColor: CHECKIN_PALETTE.silver }}
             data-testid={`comp-title-${k}`} />
      <textarea value={comp.body || ""} onChange={e => onChange({ body: e.target.value })}
                rows={10} maxLength={4000}
                className="w-full rounded border px-2 py-1 text-sm font-mono"
                style={{ borderColor: CHECKIN_PALETTE.silver }}
                data-testid={`comp-body-${k}`} />
      <div className="text-[10px] mt-1" style={{ color: "#6C6478" }}>
        {(comp.body || "").length} chars
      </div>
    </div>
  );
}

function TestPane({ label, text, testid }) {
  return (
    <div className="rounded-lg border p-3 text-[13px]"
         style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "#FAFAFB" }}
         data-testid={testid}>
      <div className="text-[10px] uppercase mb-1 font-semibold" style={{ color: CHECKIN_PALETTE.purple }}>
        {label}
      </div>
      <div className="whitespace-pre-wrap">{text || "…"}</div>
    </div>
  );
}


// ═══════════════ Conversations (admin — metadata + audit-logged reveal) ═══════════════
function ConversationsTab() {
  const [q, setQ] = useState({ email: "", state: "", access_type: "" });
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [revealed, setRevealed] = useState(null); // {conv, messages, user}

  const load = useCallback(async () => {
    setErr("");
    try {
      const out = await adminApi.conversations({ ...q, limit: 100 });
      setRows(out.items || []);
    } catch (e) { setErr(e.message); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  async function reveal(id) {
    const reason = window.prompt(
      "Reason for accessing conversation content? (required, ≥ 5 chars — audit-logged)"
    );
    if (!reason || reason.trim().length < 5) return;
    try {
      const out = await adminApi.revealConversation(id, reason.trim());
      setRevealed(out);
    } catch (e) { alert(e.message); }
  }

  return (
    <div data-testid="admin-conversations">
      <div className="flex flex-wrap gap-2 mb-3">
        <input placeholder="email" value={q.email}
               onChange={e => setQ({ ...q, email: e.target.value })}
               className="rounded border px-2 py-1 text-sm"
               style={{ borderColor: CHECKIN_PALETTE.silver }}
               data-testid="convs-filter-email" />
        <select value={q.state} onChange={e => setQ({ ...q, state: e.target.value })}
                className="rounded border px-2 py-1 text-sm"
                style={{ borderColor: CHECKIN_PALETTE.silver }}
                data-testid="convs-filter-state">
          <option value="">Any state</option>
          <option value="active">active</option>
          <option value="paused_awaiting_paid">paused</option>
          <option value="closed">closed</option>
        </select>
        <select value={q.access_type} onChange={e => setQ({ ...q, access_type: e.target.value })}
                className="rounded border px-2 py-1 text-sm"
                style={{ borderColor: CHECKIN_PALETTE.silver }}
                data-testid="convs-filter-access">
          <option value="">Any access</option>
          <option value="complimentary">complimentary</option>
          <option value="talk_credit">talk_credit</option>
          <option value="membership">membership</option>
        </select>
        <button onClick={load}
                className="px-3 py-1 rounded text-sm font-medium"
                style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                data-testid="convs-refresh">Search</button>
      </div>
      {err && <ErrPanel msg={err} />}
      <div className="rounded-xl border bg-white overflow-x-auto"
           style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: "#F5F3FA" }}>
              <th className="text-left p-2">Updated</th>
              <th className="text-left p-2">User</th>
              <th className="text-left p-2">Title</th>
              <th className="text-left p-2">State</th>
              <th className="text-left p-2">Access</th>
              <th className="text-left p-2">Msgs</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t"
                  style={{ borderColor: CHECKIN_PALETTE.silverSoft }}
                  data-testid={`convs-row-${r.id}`}>
                <td className="p-2 whitespace-nowrap">{(r.updated_at || r.created_at || "").slice(0, 19)}</td>
                <td className="p-2 truncate max-w-[180px]">{r.user_email}</td>
                <td className="p-2 truncate max-w-[220px]">{r.title || "Check-In"}</td>
                <td className="p-2">{r.state}</td>
                <td className="p-2">{r.access_type}</td>
                <td className="p-2 tabular-nums">{r.message_count}</td>
                <td className="p-2">
                  <button onClick={() => reveal(r.id)}
                          className="text-[11px] underline"
                          data-testid={`convs-reveal-${r.id}`}
                          style={{ color: CHECKIN_PALETTE.purple }}>
                    Reveal
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-sm"
                      style={{ color: "#6C6478" }}>No conversations match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {revealed && (
        <div className="mt-4 rounded-xl border p-4 bg-white"
             style={{ borderColor: CHECKIN_PALETTE.purple }}
             data-testid="convs-reveal-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide font-semibold"
                   style={{ color: CHECKIN_PALETTE.purple }}>Revealed content · audit-logged</div>
              <div className="text-sm mt-1">
                {(revealed.user || {}).email} · {revealed.messages.length} messages
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: "#6C6478" }}>
                Reason: {revealed.reason}
              </div>
            </div>
            <button onClick={() => setRevealed(null)}
                    className="text-[11px] underline"
                    style={{ color: "#6C6478" }}
                    data-testid="convs-reveal-close">
              Close
            </button>
          </div>
          <div className="mt-3 max-h-[400px] overflow-y-auto space-y-2">
            {revealed.messages.map(m => (
              <div key={m.id}
                   className="text-[12px] rounded p-2"
                   style={{
                     background: m.role === "assistant" ? "#F5F3FA" : "white",
                     border: `1px solid ${CHECKIN_PALETTE.silverSoft}`,
                   }}
                   data-testid={`convs-msg-${m.role}`}>
                <div className="text-[10px] uppercase tracking-wide mb-1"
                     style={{ color: "#6C6478" }}>
                  {m.role === "assistant" ? "Dr. C" : "User"} · {(m.created_at || "").slice(11, 19)}
                </div>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════ Safety ═══════════════
function SafetyTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    adminApi.safetyEvents(100).then(out => setRows(out.items || [])).catch(e => setErr(e.message));
  }, []);
  return (
    <div data-testid="admin-safety">
      {err && <ErrPanel msg={err} />}
      <div className="rounded-xl border bg-white overflow-x-auto"
           style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: "#F5F3FA" }}>
              <th className="text-left p-2">Time</th>
              <th className="text-left p-2">User</th>
              <th className="text-left p-2">Category</th>
              <th className="text-left p-2">Severity</th>
              <th className="text-left p-2">Action</th>
              <th className="text-left p-2">LLM bypassed</th>
              <th className="text-left p-2">Crisis copy</th>
              <th className="text-left p-2">Follow-up</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-t" style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
                <td className="p-2">{r.created_at?.slice(0,19)}</td>
                <td className="p-2">{r.user_id?.slice(0,8) || "—"}</td>
                <td className="p-2">{r.category || "—"}</td>
                <td className="p-2">{r.severity || "—"}</td>
                <td className="p-2">{r.action_taken || "—"}</td>
                <td className="p-2">{r.llm_bypassed ? "yes" : "no"}</td>
                <td className="p-2">{r.crisis_copy_delivered ? "yes" : "no"}</td>
                <td className="p-2">{r.follow_up_status || "none"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center text-sm" style={{ color: "#6C6478" }}>No safety events.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════ Settings ═══════════════
const SETTINGS_SECTIONS = [
  { key: "brand",              label: "Application branding" },
  { key: "copy",               label: "Public-facing copy" },
  { key: "greetings",          label: "Greeting options" },
  { key: "membership_display", label: "Membership display" },
  { key: "credit_pack_display",label: "Talk Credit pack display" },
  { key: "recommendation",     label: "Recommendation CTAs" },
  { key: "memory_rules",       label: "Memory rules" },
  { key: "safety_config",      label: "Safety configuration" },
  { key: "legal",              label: "Legal policy content" },
  { key: "contact",            label: "Contact / support info" },
];

function SettingsTab() {
  const [data, setData] = useState(null);
  const [section, setSection] = useState("brand");
  const [json, setJson] = useState("{}");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);

  async function reload() {
    try {
      const out = await adminApi.settings();
      setData(out);
    } catch (e) { setErr(e.message); }
  }
  useEffect(() => { reload(); }, []);

  useEffect(() => {
    if (!data) return;
    const current = (data.overrides || {})[section] || {};
    const draft = (data.drafts || []).find(d => d.section === section);
    setJson(JSON.stringify(draft?.patch || current, null, 2));
  }, [section, data]);

  async function save(publish) {
    setErr(""); setOk(""); setBusy(true);
    try {
      const patch = JSON.parse(json);
      await adminApi.saveSettings(section, patch, publish);
      setOk(publish ? "Published live." : "Draft saved.");
      await reload();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div data-testid="admin-settings">
      <div className="flex flex-wrap gap-2 mb-3">
        {SETTINGS_SECTIONS.map(s => (
          <button key={s.key} onClick={() => setSection(s.key)}
                  className="px-3 py-1 rounded text-[12px]"
                  style={{ background: section === s.key ? CHECKIN_PALETTE.purple : "white",
                            color: section === s.key ? "white" : CHECKIN_PALETTE.ink,
                            border: `1px solid ${CHECKIN_PALETTE.silverSoft}` }}
                  data-testid={`settings-section-${s.key}`}>{s.label}</button>
        ))}
      </div>
      {err && <ErrPanel msg={err} />}
      {ok && <div className="mb-2 text-sm" style={{ color: "#3F8F6A" }} data-testid="settings-ok">{ok}</div>}
      <div className="rounded-xl border bg-white p-3" style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
        <div className="text-[11px] uppercase mb-1" style={{ color: "#6C6478" }}>
          {section} · JSON patch (secrets are rejected server-side)
        </div>
        <textarea value={json} onChange={e => setJson(e.target.value)} rows={16}
                  className="w-full rounded border px-2 py-1 text-[11px] font-mono"
                  style={{ borderColor: CHECKIN_PALETTE.silver }}
                  data-testid="settings-json" />
        <div className="flex gap-2 mt-2">
          <button onClick={() => save(false)} disabled={busy}
                  className="px-3 py-1 rounded text-sm font-medium disabled:opacity-40"
                  style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                  data-testid="settings-save-draft">Save draft</button>
          <button onClick={() => save(true)} disabled={busy}
                  className="px-3 py-1 rounded text-sm font-medium disabled:opacity-40"
                  style={{ background: "#3F8F6A", color: "white" }}
                  data-testid="settings-publish">Publish</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════ Audit ═══════════════
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [prefix, setPrefix] = useState("");
  const load = useCallback(async () => {
    setErr("");
    try {
      const out = await adminApi.auditLog({ limit: 200, action_prefix: prefix || undefined });
      setRows(out.items || []);
    } catch (e) { setErr(e.message); }
  }, [prefix]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-audit">
      <div className="flex gap-2 mb-3">
        <select value={prefix} onChange={e => setPrefix(e.target.value)}
                className="rounded border px-2 py-1 text-sm" style={{ borderColor: CHECKIN_PALETTE.silver }}
                data-testid="audit-prefix">
          <option value="">All actions</option>
          <option value="entitlement">Entitlements</option>
          <option value="personality">Personality</option>
          <option value="settings">Settings</option>
          <option value="square">Square</option>
        </select>
        <button onClick={load} className="px-3 py-1 rounded text-sm font-medium"
                style={{ background: CHECKIN_PALETTE.purple, color: "white" }}
                data-testid="audit-refresh">Refresh</button>
      </div>
      {err && <ErrPanel msg={err} />}
      <div className="rounded-xl border bg-white overflow-x-auto"
           style={{ borderColor: CHECKIN_PALETTE.silverSoft }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ background: "#F5F3FA" }}>
              <th className="text-left p-2">Time</th>
              <th className="text-left p-2">Admin</th>
              <th className="text-left p-2">Action</th>
              <th className="text-left p-2">Target</th>
              <th className="text-left p-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t" style={{ borderColor: CHECKIN_PALETTE.silverSoft }} data-testid={`audit-row-${r.id}`}>
                <td className="p-2">{r.created_at?.slice(0,19)}</td>
                <td className="p-2">{r.admin_email}</td>
                <td className="p-2">{r.action}</td>
                <td className="p-2">{r.target_type} · {r.target_id?.slice(0,20)}</td>
                <td className="p-2">{r.reason || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-4 text-center text-sm" style={{ color: "#6C6478" }}>No audit rows.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════ Shared utilities ═══════════════
function ErrPanel({ msg }) {
  return (
    <div className="mb-3 rounded-lg border p-3 text-sm"
         style={{ borderColor: "#B4223C", background: "#FCEFF1", color: "#B4223C" }}
         data-testid="admin-error">
      {msg}
    </div>
  );
}
function SkeletonPanel({ testid }) {
  return (
    <div className="text-sm" style={{ color: "#6C6478" }} data-testid={testid}>
      Loading…
    </div>
  );
}
